import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';

const categoryMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryMap[c.slug] = c.name;
});

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <SEOHead
          title="分类未找到"
          description="抱歉，您访问的分类不存在。请返回首页浏览其他精彩内容。"
        />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageUrl = `https://tjcongling.cn/category/${slug}`;

  return (
    <>
      <SEOHead
        title={category.name}
        description={`浏览「${category.name}」分类下的所有文章。${category.description}`}
        keywords={`${category.name},羊宫妃那,网络流行语,二次元,词条解析`}
        canonicalUrl={pageUrl}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${category.name} - 羊宫妃那词条百科`,
            description: category.description,
            url: pageUrl,
            isPartOf: {
              '@type': 'WebSite',
              name: '羊宫妃那词条百科',
              url: 'https://tjcongling.cn',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://tjcongling.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category.name,
              },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: categoryArticles.map((article, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              url: `https://tjcongling.cn/article/${article.slug}`,
              name: article.title,
            })),
          },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 border-b border-foreground-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="mt-4 flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {category.name}
                </h1>
                <p className="text-foreground-500 mt-1 text-sm">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-b border-foreground-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-6">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-foreground-950 text-white'
                      : 'bg-white text-foreground-600 border border-foreground-200 hover:border-foreground-400'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.slug}
                      slug={article.slug}
                      title={article.title}
                      excerpt={article.excerpt}
                      coverImage={article.coverImage}
                      category={article.category}
                      categoryName={categoryMap[article.category] || article.category}
                      author={article.author}
                      publishDate={article.publishDate}
                      readTime={article.readTime}
                      tags={article.tags}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-400">该分类下暂无文章</p>
                <Link to="/" className="text-primary-500 hover:text-primary-600 mt-3 inline-block">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}